import { useState } from 'react';
import { Brain, Loader2, TrendingUp, AlertCircle, Eye, RefreshCw, Check, X } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Alert } from '../ui/Alert';
import { Progress } from '../ui/Progress';
import { aiService } from '@/services/aiService';
import { calendarService } from '@/services/calendarService';
import { useListsStore } from '@/store/listsStore';
import { AIScheduleOptimizationResult, SchedulePreviewChange, SchedulePreviewError } from '@/types/ai';
import { SchedulePreviewCalendar } from './SchedulePreviewCalendar';
import { ListSchedule } from '@/types/calendar';

interface ScheduleOptimizationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onOptimizationApplied?: () => void;
}

type ViewState = 'initial' | 'loading' | 'results' | 'preview' | 'error';

// Helper to normalize date for comparison
function normalizeDate(dateStr: string): string {
  try {
    const date = new Date(dateStr);
    return date.toISOString().split('T')[0]; // YYYY-MM-DD format
  } catch {
    return dateStr;
  }
}

// Map to store schedule lookup data for matching AI responses
interface ScheduleLookupEntry {
  scheduleId: string;
  listId: string;
  listTitle: string;
  originalDate: string;
  originalTime: string;
}

export function ScheduleOptimizationModal({ isOpen, onClose, onOptimizationApplied }: ScheduleOptimizationModalProps) {
  const [viewState, setViewState] = useState<ViewState>('initial');
  const [optimizationResult, setOptimizationResult] = useState<AIScheduleOptimizationResult | null>(null);
  const [previewChanges, setPreviewChanges] = useState<SchedulePreviewChange[]>([]);
  const [originalSchedules, setOriginalSchedules] = useState<ListSchedule[]>([]);
  const [error, setError] = useState<SchedulePreviewError | null>(null);

  const { lists } = useListsStore();

  // Find the real schedule based on AI response data
  // The AI may return different IDs, so we match by date/time/title
  const findRealSchedule = (
    opt: { scheduleId: string; originalDate: string; originalTime: string },
    lookup: ScheduleLookupEntry[]
  ): ScheduleLookupEntry | undefined => {
    // First try exact ID match
    let match = lookup.find(s => s.scheduleId === opt.scheduleId);
    if (match) return match;

    // Try matching by normalized date and time
    const normalizedOptDate = normalizeDate(opt.originalDate);
    match = lookup.find(s =>
      normalizeDate(s.originalDate) === normalizedOptDate &&
      s.originalTime === opt.originalTime
    );
    if (match) return match;

    // Try matching by just date (if only one schedule on that date)
    const dateMatches = lookup.filter(s =>
      normalizeDate(s.originalDate) === normalizedOptDate
    );
    if (dateMatches.length === 1) return dateMatches[0];

    return undefined;
  };

  // Convert optimization result to preview changes
  const buildPreviewChangesFromResult = (
    result: AIScheduleOptimizationResult,
    lookup: ScheduleLookupEntry[]
  ): SchedulePreviewChange[] => {
    const changes: SchedulePreviewChange[] = [];

    // Add moved schedules
    result.optimizedSchedules.forEach(opt => {
      const realSchedule = findRealSchedule(opt, lookup);

      if (realSchedule) {
        const list = lists.find(l => l.id === realSchedule.listId);
        changes.push({
          id: `move-${realSchedule.scheduleId}`,
          type: 'move',
          originalScheduleId: realSchedule.scheduleId,
          listId: realSchedule.listId,
          listTitle: list?.title || realSchedule.listTitle,
          originalDate: realSchedule.originalDate,
          originalTime: realSchedule.originalTime,
          newDate: opt.suggestedDate,
          newTime: opt.suggestedTime,
          duration: 60,
          reasoning: opt.reasoning,
          improvementScore: opt.improvementScore
        });
      } else {
        console.warn('Could not find matching schedule for:', opt);
      }
    });

    // Add new schedule suggestions
    result.newScheduleSuggestions.forEach((suggestion, idx) => {
      // For new suggestions, try to find list by ID or by title
      let list = lists.find(l => l.id === suggestion.listId);

      // If not found by ID, the AI might have used a made-up ID, try to find by matching title
      if (!list) {
        // Look for a list that hasn't been scheduled yet
        const scheduledListIds = new Set(lookup.map(s => s.listId));
        const unscheduledLists = lists.filter(l => !scheduledListIds.has(l.id) && l.status !== 'complete');
        if (unscheduledLists.length > 0) {
          // Use the highest priority unscheduled list as a fallback
          list = unscheduledLists.sort((a, b) => a.priority - b.priority)[0];
        }
      }

      if (list) {
        changes.push({
          id: `new-${idx}`,
          type: 'new',
          listId: list.id,
          listTitle: list.title,
          newDate: suggestion.suggestedDate,
          newTime: suggestion.suggestedTime,
          duration: suggestion.duration || 60,
          reasoning: suggestion.reasoning
        });
      } else {
        console.warn('Could not find list for new schedule suggestion:', suggestion);
      }
    });

    return changes;
  };

  const handleOptimizeWeek = async () => {
    if (!aiService.isConfigured()) {
      setError({
        code: 'API_ERROR',
        message: 'AI service not configured',
        details: 'Please add your Anthropic API key in Settings.',
        suggestion: 'Go to Settings > AI Configuration to add your API key.'
      });
      setViewState('error');
      return;
    }

    setViewState('loading');
    setError(null);

    // Snapshot current schedules for potential restoration
    setOriginalSchedules([...calendarService.getAllSchedules()]);

    try {
      const schedules = calendarService.getAllSchedules();
      const weekStart = new Date();
      weekStart.setHours(0, 0, 0, 0);

      const DEFAULT_WORKING_HOURS = {
        monday: { start: '09:00', end: '17:00' },
        tuesday: { start: '09:00', end: '17:00' },
        wednesday: { start: '09:00', end: '17:00' },
        thursday: { start: '09:00', end: '17:00' },
        friday: { start: '09:00', end: '17:00' },
        saturday: { start: '10:00', end: '16:00' },
        sunday: { start: '10:00', end: '16:00' }
      };

      // Build lookup table for matching AI responses back to real schedules
      const activeSchedules = schedules.filter(s => s.isActive && s.scheduledDate);
      const lookupEntries: ScheduleLookupEntry[] = activeSchedules.map(s => ({
        scheduleId: s.id,
        listId: s.listId,
        listTitle: lists.find(l => l.id === s.listId)?.title || '',
        originalDate: s.scheduledDate!.toISOString(),
        originalTime: s.scheduledTime || '09:00'
      }));

      const optimizationRequest = {
        weekStartDate: weekStart.toISOString(),
        schedules: activeSchedules.map(s => ({
            id: s.id,
            listId: s.listId,
            listTitle: lists.find(l => l.id === s.listId)?.title || '',
            priority: lists.find(l => l.id === s.listId)?.priority || 5,
            currentDate: s.scheduledDate!.toISOString(),
            currentTime: s.scheduledTime || '09:00',
            duration: 60,
            isLocked: false
          })),
        lists: lists
          .filter(l => l.status !== 'complete')
          .map(l => ({
            id: l.id,
            title: l.title,
            priority: l.priority,
            estimatedHours: l.estimatedHours,
            deadline: l.deadline?.toISOString(),
            goalId: l.goalId
          })),
        optimizationGoals: ['balance', 'priority', 'deadline'] as ('balance' | 'priority' | 'deadline')[],
        userContext: {
          workingHours: DEFAULT_WORKING_HOURS,
          preferredWorkStyles: ['focused', 'morning']
        }
      };

      const result = await aiService.optimizeWeeklySchedule(optimizationRequest);

      // Validate result
      if (!result.optimizedSchedules && !result.newScheduleSuggestions) {
        throw new Error('AI returned an empty optimization result');
      }

      setOptimizationResult(result);
      // Build preview changes using the lookup we just created
      const changes = buildPreviewChangesFromResult(result, lookupEntries);
      setPreviewChanges(changes);
      setViewState('results');

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';

      // Parse error type and provide helpful messages
      let errorDetails: SchedulePreviewError;

      if (errorMessage.includes('API key') || errorMessage.includes('401') || errorMessage.includes('authentication')) {
        errorDetails = {
          code: 'API_ERROR',
          message: 'Authentication failed',
          details: errorMessage,
          suggestion: 'Please check your API key in Settings.'
        };
      } else if (errorMessage.includes('parse') || errorMessage.includes('JSON') || errorMessage.includes('format')) {
        errorDetails = {
          code: 'PARSE_ERROR',
          message: 'Failed to parse AI response',
          details: errorMessage,
          suggestion: 'The AI returned an unexpected format. Try again or select a different model in Settings.'
        };
      } else if (errorMessage.includes('network') || errorMessage.includes('fetch') || errorMessage.includes('Failed to fetch')) {
        errorDetails = {
          code: 'NETWORK_ERROR',
          message: 'Network error',
          details: errorMessage,
          suggestion: 'Please check your internet connection and try again.'
        };
      } else {
        errorDetails = {
          code: 'VALIDATION_ERROR',
          message: 'Optimization failed',
          details: errorMessage,
          suggestion: 'Please try again. If the problem persists, check your schedule data.'
        };
      }

      setError(errorDetails);
      setViewState('error');
    }
  };

  const handlePreviewChanges = () => {
    setViewState('preview');
  };

  const handleApplyChanges = () => {
    if (previewChanges.length === 0) return;

    try {
      // Apply each change using the corrected schedule IDs from previewChanges
      previewChanges.forEach((change) => {
        if (change.type === 'move' && change.originalScheduleId) {
          // Update existing schedule
          calendarService.updateSchedule(change.originalScheduleId, {
            scheduledDate: new Date(change.newDate),
            scheduledTime: change.newTime
          });
        } else if (change.type === 'new') {
          // Create new schedule
          calendarService.createSchedule({
            listId: change.listId,
            type: 'one_time',
            scheduledDate: new Date(change.newDate),
            scheduledTime: change.newTime,
            isActive: true
          });
        }
      });

      // Reset state and close modal
      resetState();
      onClose();

      // Notify parent component that optimization was applied
      onOptimizationApplied?.();

    } catch (applyError) {
      console.error('Failed to apply optimizations:', applyError);
      setError({
        code: 'VALIDATION_ERROR',
        message: 'Failed to apply changes',
        details: applyError instanceof Error ? applyError.message : 'Unknown error',
        suggestion: 'Some changes may not have been applied. Please check your calendar.'
      });
      setViewState('error');
    }
  };

  const handleBackToResults = () => {
    setViewState('results');
  };

  const resetState = () => {
    setViewState('initial');
    setOptimizationResult(null);
    setPreviewChanges([]);
    setOriginalSchedules([]);
    setError(null);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  // Render based on view state
  const renderContent = () => {
    switch (viewState) {
      case 'initial':
        return renderInitialView();
      case 'loading':
        return renderLoadingView();
      case 'results':
        return renderResultsView();
      case 'preview':
        return renderPreviewView();
      case 'error':
        return renderErrorView();
    }
  };

  const renderInitialView = () => (
    <>
      <div className="space-y-4">
        <p className="text-muted-foreground">
          Let AI analyze and optimize your weekly schedule for maximum productivity
        </p>

        <div className="space-y-3">
          <p className="text-sm font-medium">The AI will analyze your:</p>
          <ul className="text-sm text-muted-foreground space-y-2 ml-4">
            <li className="flex items-center gap-2">
              <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
              Current schedule and workload distribution
            </li>
            <li className="flex items-center gap-2">
              <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
              Task deadlines and priority levels
            </li>
            <li className="flex items-center gap-2">
              <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
              Available time slots and conflicts
            </li>
            <li className="flex items-center gap-2">
              <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
              SMART goal alignment and deadlines
            </li>
          </ul>
        </div>
      </div>

      <div className="mt-6">
        <Button
          onClick={handleOptimizeWeek}
          className="w-full gap-2"
          size="lg"
        >
          <TrendingUp className="h-5 w-5" />
          Optimize This Week
        </Button>
      </div>
    </>
  );

  const renderLoadingView = () => (
    <div className="flex flex-col items-center justify-center py-12">
      <Loader2 className="h-12 w-12 animate-spin text-purple-600 mb-4" />
      <p className="text-lg font-medium">Analyzing your schedule...</p>
      <p className="text-sm text-muted-foreground mt-2">
        This may take up to 30 seconds
      </p>
    </div>
  );

  const renderResultsView = () => (
    <div className="space-y-6">
      {/* Improvement Metrics */}
      {optimizationResult && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 bg-muted rounded-lg border">
            <p className="text-sm text-muted-foreground mb-2">Workload Balance</p>
            <Progress
              value={(optimizationResult.overallImprovement.workloadBalance + 1) * 50}
              className="h-3 mb-2"
            />
            <p className="text-sm font-medium">
              {(optimizationResult.overallImprovement.workloadBalance * 100).toFixed(0)}% improvement
            </p>
          </div>
          <div className="p-4 bg-muted rounded-lg border">
            <p className="text-sm text-muted-foreground mb-2">Priority Alignment</p>
            <Progress
              value={(optimizationResult.overallImprovement.priorityAlignment + 1) * 50}
              className="h-3 mb-2"
            />
            <p className="text-sm font-medium">
              {(optimizationResult.overallImprovement.priorityAlignment * 100).toFixed(0)}% improvement
            </p>
          </div>
          <div className="p-4 bg-muted rounded-lg border">
            <p className="text-sm text-muted-foreground mb-2">Deadline Compliance</p>
            <Progress
              value={(optimizationResult.overallImprovement.deadlineCompliance + 1) * 50}
              className="h-3 mb-2"
            />
            <p className="text-sm font-medium">
              {(optimizationResult.overallImprovement.deadlineCompliance * 100).toFixed(0)}% improvement
            </p>
          </div>
        </div>
      )}

      {/* Summary of Changes */}
      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
        <h4 className="font-medium text-blue-800 mb-2">Summary</h4>
        <p className="text-sm text-blue-700">
          {previewChanges.filter(c => c.type === 'move').length} schedule(s) will be moved
          {previewChanges.filter(c => c.type === 'new').length > 0 &&
            ` and ${previewChanges.filter(c => c.type === 'new').length} new schedule(s) will be created`
          }
        </p>
      </div>

      {/* Changes List */}
      <div className="space-y-3 max-h-64 overflow-y-auto">
        <h4 className="font-medium">Proposed Changes:</h4>

        {previewChanges.map((change) => (
          <div
            key={change.id}
            className={`p-4 rounded-lg border ${
              change.type === 'new' ? 'bg-green-50 border-green-200' : 'bg-background'
            }`}
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="font-medium text-sm">
                  {change.type === 'move' ? 'Move' : 'New'}: {change.listTitle}
                </p>
                {change.type === 'move' && change.originalDate && (
                  <p className="text-sm text-muted-foreground">
                    From: {new Date(change.originalDate).toLocaleDateString()} at {change.originalTime}
                  </p>
                )}
                <p className={`text-sm ${change.type === 'new' ? 'text-green-700' : 'text-green-600'}`}>
                  To: {new Date(change.newDate).toLocaleDateString()} at {change.newTime}
                </p>
              </div>
              {change.improvementScore !== undefined && (
                <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                  +{Math.round(change.improvementScore * 100)}%
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-2">{change.reasoning}</p>
          </div>
        ))}

        {previewChanges.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-4">
            No changes suggested. Your schedule is already optimized!
          </p>
        )}
      </div>

      {/* Insights */}
      {optimizationResult?.insights && optimizationResult.insights.length > 0 && (
        <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
          <p className="text-sm font-medium text-purple-800 mb-2">AI Insights:</p>
          <div className="space-y-1">
            {optimizationResult.insights.map((insight, idx) => (
              <p key={idx} className="text-sm text-purple-700">• {insight}</p>
            ))}
          </div>
        </div>
      )}

      {/* Warnings */}
      {optimizationResult?.warnings && optimizationResult.warnings.length > 0 && (
        <Alert variant="warning">
          <AlertCircle className="h-4 w-4" />
          <div className="space-y-1">
            {optimizationResult.warnings.map((warning, idx) => (
              <p key={idx}>{warning}</p>
            ))}
          </div>
        </Alert>
      )}

      {/* Action Buttons */}
      <div className="flex gap-3">
        <Button
          onClick={handlePreviewChanges}
          className="flex-1"
          disabled={previewChanges.length === 0}
        >
          <Eye className="h-4 w-4 mr-2" />
          Preview Changes
        </Button>
        <Button
          onClick={handleOptimizeWeek}
          variant="outline"
          className="flex-1"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Try Again
        </Button>
      </div>
    </div>
  );

  const renderPreviewView = () => (
    <div className="space-y-4">
      <SchedulePreviewCalendar
        changes={previewChanges}
        originalSchedules={originalSchedules}
      />

      <div className="flex gap-3">
        <Button
          onClick={handleApplyChanges}
          className="flex-1"
        >
          <Check className="h-4 w-4 mr-2" />
          Accept Changes
        </Button>
        <Button
          onClick={handleBackToResults}
          variant="outline"
        >
          <X className="h-4 w-4 mr-2" />
          Back
        </Button>
      </div>
    </div>
  );

  const renderErrorView = () => (
    <div className="space-y-4">
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <div>
          <p className="font-medium">{error?.message}</p>
          {error?.details && (
            <p className="text-sm mt-1">{error.details}</p>
          )}
        </div>
      </Alert>

      {error?.suggestion && (
        <div className="p-4 bg-muted rounded-lg">
          <p className="text-sm font-medium">Suggestion:</p>
          <p className="text-sm text-muted-foreground">{error.suggestion}</p>
        </div>
      )}

      <div className="flex gap-3">
        <Button
          onClick={handleOptimizeWeek}
          className="flex-1"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Try Again
        </Button>
        <Button
          onClick={handleClose}
          variant="outline"
          className="flex-1"
        >
          Cancel
        </Button>
      </div>
    </div>
  );

  const getSubtitle = () => {
    switch (viewState) {
      case 'preview':
        return 'Preview your optimized schedule';
      case 'results':
        return 'Review the suggested optimizations';
      case 'error':
        return 'Something went wrong';
      default:
        return 'Let AI analyze and optimize your weekly schedule';
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size={viewState === 'preview' ? 'xl' : 'lg'}
      title="AI Schedule Optimizer"
      showCloseButton={true}
      scrollBehavior="inside"
    >
      <div className="flex items-center gap-2 mb-6">
        <Brain className="h-6 w-6 text-purple-600" />
        <span className="text-muted-foreground">
          {getSubtitle()}
        </span>
      </div>

      {renderContent()}
    </Modal>
  );
}
